import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFeatureValue } from '@growthbook/growthbook-react'

import { OptionCard } from 'components/OptionCard'
import {
  TERMS_LINKS_GROUP_VARIANT,
  TermsLinksGroup,
} from 'components/TermsLinksGroup'
import {
  TrustPilotBannerVariant1,
  TrustPilotBannerVariant2,
} from 'components/TrustpilotBanner'

import { useCohortInfo } from 'hooks/useCohortInfo'
import { useDelayedContinue } from 'hooks/useDelayedContinue'
import { useGetPageInfo } from 'hooks/useGetPageInfo'
import { useNextStep } from 'hooks/useNextStep'

import { TPageProps } from 'models/common.model'

import { CustomPageId } from 'page-constants'
import {
  Color,
  GROWTHBOOK_FEATURE_KEY,
  Language,
  OptionType,
} from 'root-constants'

import { StyledSelectGenderKarma as S } from './SelectGenderKarma.styles'
import {
  COHORT_TO_VERSION_MAP,
  GENDER_OPTIONS_BY_VERSION,
  KARMA_WELCOME_PAGE_TEST_SEGMENTS,
} from './constants'

export const SelectGenderKarma: React.FC<TPageProps> = ({
  pageId,
  nextPagePath,
}) => {
  const { t } = useTranslation()
  const { cohortToUse } = useCohortInfo()
  const { hasProgressbar } = useGetPageInfo()

  const { isAnswersDisabled, handleChange, setIsAnswersDisabled } =
    useDelayedContinue()

  const karmaWelcomePageTestValue = useFeatureValue(
    GROWTHBOOK_FEATURE_KEY.KARMA_15_WELCOME_PAGE_TRUSTPILOT_TEST,
    KARMA_WELCOME_PAGE_TEST_SEGMENTS.DEFAULT,
  )

  const [answer, setAnswer] = useState<string>('')

  const context =
    COHORT_TO_VERSION_MAP[cohortToUse] || COHORT_TO_VERSION_MAP.default
  const genderOptions = GENDER_OPTIONS_BY_VERSION[context]

  const handleContinue = useNextStep({
    pageId: CustomPageId.GENDER,
    question: t('onboarding.selectGenderKarma.title', {
      lng: Language.EN,
      context,
    }),
    nextPagePath,
    isFirstPage: true,
  })

  const optionProps = {
    name: pageId,
    type: OptionType.RADIO,
    onChange: (value: string) => {
      handleChange(value, handleContinue)
      setIsAnswersDisabled(true)
      setAnswer(value)
    },
  }

  return (
    <S.Container $padding={hasProgressbar ? '24px 24px 0' : '16px 24px 0'}>
      {karmaWelcomePageTestValue ===
        KARMA_WELCOME_PAGE_TEST_SEGMENTS.VARIANT_B && (
        <TrustPilotBannerVariant1 />
      )}

      {karmaWelcomePageTestValue ===
        KARMA_WELCOME_PAGE_TEST_SEGMENTS.VARIANT_C && (
        <TrustPilotBannerVariant2 />
      )}

      <S.Title>{t('onboarding.selectGenderKarma.title', { context })}</S.Title>
      <S.Subtitle>
        {t('onboarding.selectGenderKarma.subtitle', { context })}
      </S.Subtitle>

      <S.CardsContainer>
        {genderOptions.map(({ value, text, imageUrl }) => (
          <OptionCard
            key={value}
            imageUrl={imageUrl}
            value={value}
            label={t(text)}
            checked={answer === value}
            disabled={isAnswersDisabled}
            $backgroundColor={Color.SKY_DARK}
            {...optionProps}
          />
        ))}
      </S.CardsContainer>

      <TermsLinksGroup
        titleKey="commonComponents.termsGenderTitle"
        variant={TERMS_LINKS_GROUP_VARIANT.DARK}
      />

      <S.TermsNote>{t('commonComponents.termsNote')}</S.TermsNote>
    </S.Container>
  )
}
